import { getQueryClient } from 'app/get-query-client';
import { useMarketBuilderStore } from 'lib/stores/marketBuilderStore';
import { startSessionTeardownEffects } from 'lib/stores/sessionTeardownEffects';
import { applyForcedDisconnectTeardown, loadUserSession, resetUserSession } from 'lib/stores/userSessionStore';
import { disposeDatadog } from 'lib/telemetry/datadogInit';
import { bankAccountsKeys } from 'queries/bankAccounts';
import { bankTransfersKeys } from 'queries/bankTransfers';
import { creditHistoryDerivedKeys } from 'queries/creditHistory';
import { infoRequestKeys } from 'queries/infoRequests';
import { liquidityProgramCoverageKeys } from 'queries/liquidityProgramCoverageKeys';
import { refundKeys } from 'queries/refunds';
import { ssnStatusKeys } from 'queries/ssn';
import { stopTradingSync } from 'stores/tradingSync';
import { useBankFlowStore } from 'stores/zustand/bankFlowStore';
import { useBannerStore } from 'stores/zustand/bannerStore';
import { attachColorPaletteBridge } from 'stores/zustand/colorPaletteBridge';
import { useCommentComposerStore } from 'stores/zustand/commentComposerStore';
import { useCryptoFlowStore } from 'stores/zustand/cryptoFlowStore';
import { useDailySessionTimeLimitRequestDialogStore } from 'stores/zustand/dailySessionTimeLimitRequestDialogStore';
import { usePaymentsFlowStore } from 'stores/zustand/paymentsFlowStore';
import { useReferralStore } from 'stores/zustand/referralStore';
import { useRegistrationStore } from 'stores/zustand/registrationStore';
import { useSocialOnboardingStore } from 'stores/zustand/socialOnboardingStore';
import { useWithdrawalDialogStore } from 'stores/zustand/withdrawalDialogStore';
import { disposeLegacyExchangeWsClient, legacyExchangeWsClient } from 'transport/wsClients';

import { registerAppLogoutTeardown } from './appLogoutTeardown';

/**
 * Shared teardown body for the app's global feature state.
 *
 * `endsSession` separates a real session end — explicit logout
 * (`userSessionStore.endSession`) and forced disconnect
 * (`applyForcedDisconnectTeardown`) — from `stop()`'s unmount teardown. Only the
 * former may end the registration session, because that also drops the CSRF
 * token and the browser-persisted signup fields. `AppEffectsBootstrap` unmounts
 * whenever the last `Providers`-using route does (navigating to a provider-less
 * route such as `/wrapped`, or a StrictMode remount), and wiping a still-signed-in
 * user's credentials and resumable signup there would be a bug, not cleanup.
 *
 * Deliberately stateless: it only touches module-level singletons (the zustand
 * flow stores, the query cache, the legacy socket), so it is safe to call
 * without a live `startAppEffects` handle and needs no per-instance closure.
 */
function resetAppState(endsSession: boolean): void {
    resetUserSession();
    useMarketBuilderStore.getState().actions.resetDraft();
    useBankFlowStore.getState().actions.dispose();
    useCryptoFlowStore.getState().actions.dispose();
    usePaymentsFlowStore.getState().actions.dispose();
    useBannerStore.getState().actions.dispose();
    // endSession(), not dispose(): the forced-disconnect path (maintenance /
    // unavailability) doesn't bump `unauthorizedResponseToken`, so nothing else
    // clears the registration session id or the sessionStorage/localStorage
    // signup fields. dispose() alone would leave them for a reload or a second
    // signup in the same tab to resume with the previous applicant's data.
    //
    // It also clears the CSRF token, which must happen before the
    // legacyExchangeWsClient.disconnect() below — that reopen reads the token at
    // connect time and would otherwise re-authenticate the session being ended.
    if (endsSession) {
        // The withdrawal dialog is a module-level singleton holding the destination the money
        // leaves for -- bankId / cardId / paymentId / cryptoWalletId / stripeCardPayoutMethodId --
        // plus the entered amount, the current step and the Mesh dialog flags. None of it was
        // cleared on logout, so the next user in the same tab inherited the previous user's
        // withdrawal destination. Reset the whole store, not just the Mesh subset: the other
        // identifiers are the ones `WithdrawalDestination`'s own doc comment warns about.
        //
        // Paired with cryptoFlowStore.endSession(), which clears the selected Mesh wallet
        // address. Both must happen in the same pass, or an open Mesh amount dialog survives
        // without its destination (or vice versa).
        useWithdrawalDialogStore.getState().actions.endSession();
        useCryptoFlowStore.getState().actions.endSession();
        useRegistrationStore.getState().actions.endSession();
        // Another module-level singleton with no session teardown: a comment the previous account
        // just posted, and its open reply target, would otherwise render to the next user in the
        // same tab before their own comments query settles -- bypassing the blocked-author filter,
        // since pending comments are assumed to be the viewer's own. Session-end only: the unmount
        // path leaves a still-signed-in user's pending comments alone.
        useCommentComposerStore.getState().actions.endSession();
    }
    useSocialOnboardingStore.getState().actions.dispose();
    // Runs on every logout — without this, logging back into the same account
    // within the default 120s staleTime window would render stale bank account/
    // transfer data from the ended session.
    getQueryClient().removeQueries({ queryKey: bankAccountsKeys.all });
    getQueryClient().removeQueries({ queryKey: bankTransfersKeys.all });
    // These four are cached with staleTime: Infinity (never refetch once
    // fresh), so unlike most migrated user queries they won't self-correct on
    // re-login without an explicit clear.
    getQueryClient().removeQueries({ queryKey: creditHistoryDerivedKeys.all });
    getQueryClient().removeQueries({ queryKey: refundKeys.all });
    getQueryClient().removeQueries({ queryKey: ssnStatusKeys.all });
    // Without this, logging back into the same account would reuse the cached
    // list and skip the blocking modal for a request the server added since.
    getQueryClient().removeQueries({ queryKey: infoRequestKeys.all });
    // A maker's own programs and coverage. Keyed by user as well, so the next account never reads
    // them; removing them also cancels a fetch still in flight under the ended session.
    getQueryClient().removeQueries({ queryKey: liquidityProgramCoverageKeys.all });
    // Runs on every logout (endSession / forced disconnect) and on unmount
    // teardown via stop(). Detaches the legacy stores' subscriptions from their
    // socket (they re-attach on the next login connect()). The socket is rotated
    // because its handshake can carry authentication; public event-contract
    // streams immediately resume on the replacement socket.
    legacyExchangeWsClient.disconnect();
}

/** Session-end teardown: explicit logout and forced disconnect. */
export function resetAppStateOnLogout(): void {
    useDailySessionTimeLimitRequestDialogStore.getState().actions.reset();
    resetAppState(true);
}

/**
 * Starts the app's global client-side effects and returns a `stop()` that fully
 * tears them down. Owns the color palette bridge, auth-driven session teardown,
 * the referral cookie bootstrap, and the initial session restore.
 *
 * Client-only: invoked from `AppEffectsBootstrap`'s mount effect, so it never
 * runs during SSR/prerender (the color palette bridge's stateful subscriptions
 * are browser-only anyway, and first paint is handled by the pre-hydration
 * script in `app/layout.tsx`).
 */
export function startAppEffects(): () => void {
    registerAppLogoutTeardown(resetAppStateOnLogout);

    const disposeColorPaletteBridge = attachColorPaletteBridge();
    const stopSessionTeardownEffects = startSessionTeardownEffects(() => applyForcedDisconnectTeardown());

    useReferralStore.getState().actions.initFromCookie();
    // Bootstrap a session restored from cookies/localStorage on browser reload —
    // fetches the profile, starts/connects the FE session socket, subscribes
    // user_orders, and seeds the SSN/user-properties caches. Safe to call
    // unconditionally: it no-ops when there's no userId, and the fresh-login path
    // (handleLoginSuccess -> loadUserSession) runs long after mount, so this
    // doesn't double-fire on login.
    void loadUserSession();

    return function stop(): void {
        // startTradingSync()/stopTradingSync() are reference-counted against
        // useTradingSync's single mount effect. resetAppStateOnLogout() also runs
        // on every logout while the bootstrap stays mounted, so stopping trading
        // sync there would permanently kill it until a remount. Only stop it here.
        stopTradingSync();
        stopSessionTeardownEffects();
        // `false`: unmounting the bootstrap is not a session end. The user may
        // still be signed in — this fires on navigation to a provider-less route
        // — so the registration session (CSRF token, resumable signup) stays.
        resetAppState(false);
        // The color palette bridge's subscriptions read live state at call time,
        // so they stay correct across a logout -> login cycle within the same
        // mount — unlike resetAppStateOnLogout(), this must not run on every
        // logout. Only tear it down here, on unmount.
        disposeColorPaletteBridge();
        // Forget the legacy client's subscription registrations entirely so a
        // future mount starts clean (resetAppStateOnLogout only detached them,
        // keeping registrations for post-logout re-login).
        disposeLegacyExchangeWsClient();
        disposeDatadog();
    };
}
